
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Button, Modal } from '@/components/ui';
import { User } from '@/types';
import { formatFullName } from '@/lib/format';

export interface RecordingConsentModalProps {
  isOpen: boolean;
  user: User;
  onComplete: () => void;
  /** When false, the modal cannot be dismissed until consent is submitted. */
  dismissible?: boolean;
  onClose?: () => void;
}

type ConsentOption = 'video' | 'audio' | 'either';

function SignaturePad({
  onChange,
}: {
  onChange: (hasSignature: boolean) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);

  const getPoint = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0];
      if (!touch) return null;
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    const point = getPoint(e);
    if (!ctx || !point) return;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    const point = getPoint(e);
    if (!ctx || !point) return;
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    onChange(true);
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Signature</label>
      <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={600}
          height={160}
          className="w-full h-40 touch-none cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={clear}>
        Clear signature
      </Button>
    </div>
  );
}

export function RecordingConsentModal({
  isOpen,
  user,
  onComplete,
  dismissible = false,
  onClose,
}: RecordingConsentModalProps) {
  const consentType = (user.recordingConsentType ?? 'either') as ConsentOption;
  const leaderName = formatFullName(user.firstName, user.lastName) || 'Prayer Partner';
  const [printedName, setPrintedName] = useState(leaderName);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPrintedName(leaderName);
      setHasSignature(false);
      setError(null);
    }
  }, [isOpen, leaderName]);

  const handleSubmit = useCallback(async () => {
    const canvas = canvasContainerRef.current?.querySelector('canvas');
    if (!canvas || !hasSignature || !printedName.trim()) {
      setError('Please enter your printed name and sign the form.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const signatureData = (canvas as HTMLCanvasElement).toDataURL('image/png');
    const res = await api.post('/me/recording-consent', {
      printedName: printedName.trim(),
      signatureData,
    });
    if (res.data) {
      onComplete();
    } else {
      setError(res.error || 'Failed to submit consent');
    }
    setSubmitting(false);
  }, [hasSignature, printedName, onComplete]);

  const today = new Date().toLocaleDateString();

  const handleClose = () => {
    if (dismissible) {
      onClose?.();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Prayer Recording Consent"
      size="lg"
      closeOnOverlayClick={dismissible}
      closeOnEscape={dismissible}
      showCloseButton={dismissible}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Thank you for your ministry to pray for others at Noon Prayer. We are grateful for your
          willingness to minister through personalized prayer recordings.
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          I, <strong>{leaderName}</strong>, give permission to Times Square Church to record my voice,
          image, likeness, and words as I share prayer responses for individuals who submit prayer requests.
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          These recordings will be shared by private link or text message with the individual who
          requested prayer.
        </p>
        <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
          <p>I understand that I may choose to participate by:</p>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={consentType === 'video'} readOnly disabled className="rounded" />
            video recording
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={consentType === 'audio'} readOnly disabled className="rounded" />
            audio-only recording
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={consentType === 'either'} readOnly disabled className="rounded" />
            either video or audio recording
          </label>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          I understand that while Times Square Church may disable the link for the recording after
          approximately 7 days, we cannot control whether the recipient copies, downloads, screen-records,
          forwards, posts, or otherwise shares it. I understand that Times Square Church is not
          responsible for how a recording is used or shared after it is delivered.
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          I may choose to stop participating in future recordings by notifying the Creative Department
          staff. I understand this will not affect recordings already shared.
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          By signing below, I give consent for this use of my image and/or voice as described above.
        </p>

        <div ref={canvasContainerRef}>
          <SignaturePad onChange={setHasSignature} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="printed-name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Printed Name
            </label>
            <input
              id="printed-name"
              value={printedName}
              onChange={(e) => setPrintedName(e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Date</label>
            <input
              value={today}
              readOnly
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm"
            />
          </div>
        </div>

        {error && <p className="text-sm text-amber-600 dark:text-amber-400">{error}</p>}

        <div className="flex justify-end gap-2">
          {dismissible && (
            <Button variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSubmit} disabled={submitting || !hasSignature || !printedName.trim()}>
            {submitting ? 'Submitting...' : 'Submit consent'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
